import clsx from 'clsx'
import { useContext, type ReactElement } from 'react'

import { useBrowserService } from '@lib/hooks/services'

import type { TypeAssetImageEntry } from '@contentful-types/asset-image'

import { ImageContext } from '@contexts/image-context'
import { SectionContext } from '@contexts/section-context'

import { renderRichTextMultiLineContent } from '@components/renderer/rich-text'
import { Figure } from '@components/ui-core/figure/figure'
import { Picture } from '@components/ui-core/picture/picture'

import { getContentfulAssetProps, getPictureAttributes } from './utils'

import styles from './asset-image.module.css'

type AssetImageProps = {
  className?: string
  data: TypeAssetImageEntry
}

export function AssetImage({ className, data }: AssetImageProps): ReactElement {
  const { asset: assetDesktop, assetMobile, description, isRounded, title = '' } = data.fields
  const { isMobile } = useBrowserService()
  const { cols } = useContext(ImageContext)
  const { isHero } = useContext(SectionContext)

  const fetchPriority = isHero ? 'high' : 'auto'
  const loading = isHero ? 'eager' : 'lazy'

  const asset = isMobile && assetMobile ? assetMobile : assetDesktop
  const contentfulAssetProps = getContentfulAssetProps(asset)
  const { src, width, height, srcSets, sizes } = getPictureAttributes({
    width: contentfulAssetProps.width,
    height: contentfulAssetProps.height,
    url: contentfulAssetProps.url,
    isSvg: contentfulAssetProps.isSvg,
    cols,
  })

  const renderedDescription = description ? renderRichTextMultiLineContent(description) : ''

  return (
    <Figure caption={renderedDescription}>
      <Picture
        className={clsx(isRounded ? styles.roundedImage : '', className)}
        alt={title}
        src={src}
        fetchPriority={fetchPriority}
        loading={loading}
        height={height}
        width={width}
        srcSets={srcSets}
        sizes={sizes}
        responsive={!contentfulAssetProps.isSvg}
      />
    </Figure>
  )
}
